import React from 'react';
import { Helmet } from 'react-helmet';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  FileText,
  Info,
  Mail,
  Phone,
  RefreshCw,
  Shield,
} from 'lucide-react';

import { useContact, useSiteSettings } from '../context/ContactContext';
import { trackCall } from '../utils/callTracker';

import './CancellationChanges.css';

function CancellationChanges() {
  const { contactSettings } = useContact();
  const { siteSettings } = useSiteSettings();
  const tfn = contactSettings?.tfn || siteSettings?.tfn || '+1-888-859-0441';

  const telHref = `tel:${tfn.replace(/[^0-9+]/g, '')}`;

  const onHeroCallClick = () => {
    trackCall(window.location.pathname, 'cancellation-hero');
  };

  const onCtaCallClick = () => {
    trackCall(window.location.pathname, 'cancellation-cta');
  };

  return (
    <div className="cancellation-changes-page">
      <Helmet>
        <title>Flight Cancellation & Changes | Flight Reservation Centre</title>
        <meta
          name="description"
          content="Need to cancel or change your flight? Learn about airline cancellation policies, change fees, refund processes, and how Flight Reservation Centre can help manage your booking modifications."
        />
        <meta
          name="keywords"
          content="flight cancellation, change flight, airline refund, cancel ticket, flight modification, rebooking, cancellation policy, change fee"
        />
      </Helmet>

      <section className="cc-hero">
        <div className="cc-hero-overlay" />
        <div className="cc-hero-content">
          <h1>Flight Cancellations & Changes</h1>
          <p className="cc-hero-subtitle">
            Need to modify or cancel your booking? We&apos;re here to help you navigate airline
            policies and process your request quickly.
          </p>
          <div className="cc-hero-actions">
            <a href={telHref} className="cc-btn-primary" onClick={onHeroCallClick}>
              <Phone size={20} />
              Call Now: {tfn}
            </a>
            <a href="#how-it-works" className="cc-btn-secondary">
              Learn How It Works
            </a>
          </div>
        </div>
      </section>

      <section className="cc-quick-info">
        <div className="cc-container">
          <div className="cc-info-grid">
            <div className="cc-info-card">
              <div className="cc-info-icon" style={{ background: '#667eea' }}>
                <Clock size={28} />
              </div>
              <h3>24-Hour Rule</h3>
              <p>
                Cancel within 24 hours of booking for a full refund (for flights 7+ days away)
              </p>
            </div>

            <div className="cc-info-card">
              <div className="cc-info-icon" style={{ background: '#f093fb' }}>
                <RefreshCw size={28} />
              </div>
              <h3>Same-Day Changes</h3>
              <p>Most airlines allow same-day flight changes with reduced fees</p>
            </div>

            <div className="cc-info-card">
              <div className="cc-info-icon" style={{ background: '#4facfe' }}>
                <Shield size={28} />
              </div>
              <h3>Travel Insurance</h3>
              <p>Some cancellations may be covered by travel protection plans</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cc-how-it-works" id="how-it-works">
        <div className="cc-container">
          <div className="cc-section-header">
            <h2>How to Cancel or Change Your Flight</h2>
            <p>Follow these simple steps to modify your booking</p>
          </div>

          <div className="cc-steps">
            <div className="cc-step">
              <div className="cc-step-number">1</div>
              <div className="cc-step-content">
                <h3>Contact Us</h3>
                <p>
                  Call our customer service team at <strong>{tfn}</strong> or email us with your
                  booking details
                </p>
              </div>
            </div>

            <div className="cc-step">
              <div className="cc-step-number">2</div>
              <div className="cc-step-content">
                <h3>Provide Information</h3>
                <p>Have your confirmation number, passenger names, and desired changes ready</p>
              </div>
            </div>

            <div className="cc-step">
              <div className="cc-step-number">3</div>
              <div className="cc-step-content">
                <h3>Review Options</h3>
                <p>
                  Our agents will check airline policies and provide available options with fees
                </p>
              </div>
            </div>

            <div className="cc-step">
              <div className="cc-step-number">4</div>
              <div className="cc-step-content">
                <h3>Confirm & Process</h3>
                <p>
                  Once you approve, we&apos;ll process the cancellation or change and send you
                  confirmation
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cc-cancellation-types">
        <div className="cc-container">
          <h2>Cancellation Options</h2>
          <p className="cc-section-subtitle">
            Different fare types offer different cancellation flexibility
          </p>

          <div className="cc-types-grid">
            <div className="cc-type-card refundable">
              <CheckCircle className="cc-card-icon" />
              <h3>Refundable Tickets</h3>
              <p>Full refund to original payment method</p>
              <ul>
                <li>No cancellation fees</li>
                <li>Refund processed in 7-14 business days</li>
                <li>Best flexibility option</li>
              </ul>
            </div>

            <div className="cc-type-card non-refundable">
              <RefreshCw className="cc-card-icon" />
              <h3>Non-Refundable Tickets</h3>
              <p>Travel credit minus applicable fees</p>
              <ul>
                <li>Credit valid for 1 year</li>
                <li>Change/cancellation fees may apply</li>
                <li>May require rebooking with fare difference</li>
              </ul>
            </div>

            <div className="cc-type-card basic-economy">
              <AlertCircle className="cc-card-icon" />
              <h3>Basic Economy</h3>
              <p>Most restrictive fare type</p>
              <ul>
                <li>Limited or no changes allowed</li>
                <li>May forfeit ticket value</li>
                <li>Exceptions for airline schedule changes</li>
              </ul>
            </div>

            <div className="cc-type-card special-circumstances">
              <Calendar className="cc-card-icon" />
              <h3>Special Circumstances</h3>
              <p>Exceptions for emergencies</p>
              <ul>
                <li>Medical emergencies (documentation required)</li>
                <li>Weather disruptions</li>
                <li>Airline schedule changes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="cc-changes-section">
        <div className="cc-container">
          <h2>Common Flight Changes</h2>
          <p className="cc-section-subtitle">We can help with various types of modifications</p>

          <div className="cc-changes-grid">
            <div className="cc-change-card">
              <Calendar className="cc-card-icon" />
              <h3>Date Changes</h3>
              <p>Change your travel dates to better suit your schedule.</p>
              <ul>
                <li>Fare difference may apply</li>
                <li>Change fees vary by airline</li>
                <li>Some fares allow free changes</li>
              </ul>
            </div>

            <div className="cc-change-card">
              <Clock className="cc-card-icon" />
              <h3>Time Changes</h3>
              <p>Switch to an earlier or later flight on the same day.</p>
              <ul>
                <li>Subject to seat availability</li>
                <li>Same-day confirmed changes available</li>
                <li>Standby options for same route</li>
              </ul>
            </div>

            <div className="cc-change-card">
              <FileText className="cc-card-icon" />
              <h3>Route Changes</h3>
              <p>Modify your origin, destination, or add connections.</p>
              <ul>
                <li>Fare difference will apply</li>
                <li>New ticket may be required</li>
                <li>Change fees vary by airline</li>
              </ul>
            </div>

            <div className="cc-change-card">
              <CreditCard className="cc-card-icon" />
              <h3>Name Corrections</h3>
              <p>Fix spelling errors or legal name changes.</p>
              <ul>
                <li>Minor corrections are often free</li>
                <li>Full name changes may not be allowed</li>
                <li>Documentation may be required</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="cc-fees-section">
        <div className="cc-container">
          <h2>Typical Cancellation & Change Fees</h2>
          <p className="cc-fees-subtitle">
            Fees vary by airline, route, and fare class. Contact us for exact pricing.
          </p>

          <div className="cc-fees-table">
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Domestic</th>
                  <th>International</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Change Fee</td>
                  <td>$0 - $200</td>
                  <td>$200 - $750</td>
                  <td>Plus fare difference</td>
                </tr>
                <tr>
                  <td>Cancellation Fee</td>
                  <td>$0 - $200</td>
                  <td>$200 - $750</td>
                  <td>Travel credit issued</td>
                </tr>
                <tr>
                  <td>Same-Day Change</td>
                  <td>$75 - $100</td>
                  <td>$150 - $200</td>
                  <td>Subject to availability</td>
                </tr>
                <tr>
                  <td>24-Hour Cancellation</td>
                  <td>FREE</td>
                  <td>FREE</td>
                  <td>Within 24hrs of booking</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="cc-fee-note">
            <Info size={20} />
            <p>
              Many airlines have eliminated change fees for most domestic tickets. International
              and basic economy fares may still have fees.
            </p>
          </div>
        </div>
      </section>

      <section className="cc-faq-section">
        <div className="cc-container">
          <h2>Frequently Asked Questions</h2>

          <div className="cc-faq-grid">
            <div className="cc-faq-item">
              <h3>Can I get a full refund if I cancel my flight?</h3>
              <p>
                It depends on your fare type. Refundable tickets allow full refunds.
                Non-refundable tickets typically provide travel credit minus any fees. Within 24
                hours of booking, most tickets are fully refundable.
              </p>
            </div>

            <div className="cc-faq-item">
              <h3>How long do I have to use travel credit?</h3>
              <p>
                Most airlines issue travel credits valid for 1 year from the original ticket
                issue date. Some may extend this period. Credits are typically non-transferable
                and must be used by the original passenger.
              </p>
            </div>

            <div className="cc-faq-item">
              <h3>What if the airline cancels my flight?</h3>
              <p>
                If the airline cancels your flight, you&apos;re entitled to a full refund regardless
                of fare type. You can also choose to be rebooked on the next available flight at
                no charge.
              </p>
            </div>

            <div className="cc-faq-item">
              <h3>Can I cancel just one passenger on a multi-passenger booking?</h3>
              <p>
                Yes, individual passengers can be cancelled from group bookings. Fees will apply
                per passenger cancelled. Contact us to process partial cancellations.
              </p>
            </div>

            <div className="cc-faq-item">
              <h3>What documents do I need to change my flight?</h3>
              <p>
                You&apos;ll need your confirmation number, passenger names, and payment method used
                for booking. For name corrections, valid ID may be required.
              </p>
            </div>

            <div className="cc-faq-item">
              <h3>Can I change my international flight to a different destination?</h3>
              <p>
                Yes, but this usually requires cancelling the original ticket and rebooking.
                Change fees, fare differences, and new taxes will apply. Contact us for the best
                options.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="cc-cta-section">
        <div className="cc-container">
          <div className="cc-cta-box">
            <h2>Need Help with Your Booking?</h2>
            <p>Our experienced team is ready to assist with cancellations, changes, and refunds</p>
            <div className="cc-cta-actions">
              <a href={telHref} className="cc-cta-btn phone" onClick={onCtaCallClick}>
                <Phone size={22} />
                <div>
                  <span>Call Us Now</span>
                  <strong>{tfn}</strong>
                </div>
              </a>
              <a href="mailto:support@flightreservationcentre.com" className="cc-cta-btn email">
                <Mail size={22} />
                <div>
                  <span>Email Support</span>
                  <strong>support@flightreservationcentre.com</strong>
                </div>
              </a>
            </div>
            <p className="cc-cta-hours">Available 24/7 • Average response time: Under 5 minutes</p>
          </div>
        </div>
      </section>

      <section className="cc-disclaimer">
        <div className="cc-container">
          <p>
            <strong>Important Disclaimer:</strong> Flight Reservation Centre is an independent travel agency.
            Cancellation and change policies are determined by individual airlines. Fees and refund
            eligibility vary based on fare rules, airline policies, and ticket type. Processing
            times for refunds depend on the airline and payment method. We recommend reviewing
            your fare rules before requesting changes. Contact our customer service team for
            specific information about your booking.
          </p>
        </div>
      </section>
    </div>
  );
}

export default CancellationChanges;
